import { Minus, Plus, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFontSize } from "@/hooks/use-font-size";
import { useHapticFeedback } from "@/hooks/use-haptic";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface FontSizeControlsProps {
  className?: string;
}

export function FontSizeControls({ className }: FontSizeControlsProps) {
  const { increaseFontSize, decreaseFontSize, resetFontSize } = useFontSize();
  const { triggerHaptic } = useHapticFeedback();

  const handleDecrease = async () => {
    try {
      await triggerHaptic('selection');
      decreaseFontSize();
    } catch (error) {
      console.warn('[FontSizeControls] Haptic feedback failed:', error);
      decreaseFontSize();
    }
  };

  const handleIncrease = async () => {
    try {
      await triggerHaptic('selection');
      increaseFontSize();
    } catch (error) {
      console.warn('[FontSizeControls] Haptic feedback failed:', error);
      increaseFontSize();
    }
  };

  const handleReset = async () => {
    try {
      await triggerHaptic('warning');
      resetFontSize();
    } catch (error) {
      console.warn('[FontSizeControls] Haptic feedback failed:', error);
      resetFontSize();
    }
  };

  const ButtonWithTooltip = ({ onClick, label, icon: Icon }: { 
    onClick: () => void, 
    label: string, 
    icon: React.ElementType 
  }) => (
    <div className="w-full md:w-auto">
      <div className="hidden md:block w-full">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              onClick={onClick}
              aria-label={label}
              className="w-full h-10 md:w-10"
            >
              <Icon className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {label}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="md:hidden w-full">
        <Button
          variant="outline"
          size="icon"
          onClick={onClick}
          aria-label={label}
          className="w-full h-10 flex items-center justify-center p-0"
        >
          <Icon className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className={cn("grid grid-cols-3 gap-0.5 md:flex md:items-center md:gap-1", className)}>
      <ButtonWithTooltip 
        onClick={handleDecrease} 
        label="Decrease" 
        icon={Minus} 
      />
      <ButtonWithTooltip 
        onClick={handleReset} 
        label="Reset" 
        icon={RotateCcw} 
      />
      <ButtonWithTooltip 
        onClick={handleIncrease} 
        label="Increase" 
        icon={Plus} 
      />
    </div>
  );
}